import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { toast } from 'sonner';
import { ItemCategory } from './itemCategoryApi';
import { HsnCode } from './hsnApi';
import { rawItemVariantApiService } from './rawItemVariantApi';

export interface RawItem {
  categoryName: string;
  id: number;
  itemName: string;
  itemUnit: string;
  type: string | null;
  sku: string;
  color: string;
  shape: string;
  size: string;
  grade: string;
  weight: number;
  minBufferStock: number;
  applicableIsStd: string | null;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  itemCategory: ItemCategory;
  hsn: HsnCode;
}

export interface RawItemResponse extends PaginatedResponse<RawItem> {}

export interface RawItemCreateRequest {
  itemName: string;
  itemUnit: string;
  type?: string;
  sku: string;
  color: string;
  shape: string;
  size: string;
  grade: string;
  weight: number;
  minBufferStock: number;
  applicableIsStd?: string;
  isActive?: boolean;
  itemCategoryId: number;
  hsnId: number;
  createdBy: number;
  updatedBy: number;
}

export interface RawItemUpdateRequest extends RawItemCreateRequest {
  id: number;
}

export interface RawItemRateHistoryDto {
  purchaseDate: string;
  unitPrice: number;
  supplierId?: number;
  supplierName?: string;
  poNumber?: string;
  poId?: number;
  quantity?: number;
}

class RawItemApiService extends BaseApiService {
  async getRawItems(): Promise<RawItem[]> {
    try {
      const response = await this.makeRequest<RawItemResponse>(
        API_ENDPOINTS.RAW_ITEMS.GET_ALL,
        { method: HTTP_METHODS.GET }
      );
      // console.log(response)
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch raw items');
      }
    } catch (error) {
      // console.error('Error fetching raw items:', error);
      toast.error('Failed to fetch raw items');
      throw error;
    }
  }

  async getRawItemById(id: string): Promise<RawItem> {
    try {
      const response = await this.makeRequest<{ success: boolean; message: string; data: RawItem }>(
        API_ENDPOINTS.RAW_ITEMS.GET_BY_ID(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch raw item');
      }
    } catch (error) {
      // console.error('Error fetching raw item:', error);
      toast.error('Failed to fetch raw item details');
      throw error;
    }
  }

  async createRawItem(rawItemData: RawItemCreateRequest): Promise<RawItem> {
    try {
      const response = await this.makeRequest<{ success: boolean; message: string; data: RawItem }>(
        API_ENDPOINTS.RAW_ITEMS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(rawItemData)
        }
      );
      
      if (response.success) {
        toast.success('Raw item created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create raw item');
      }
    } catch (error) {
      // console.error('Error creating raw item:', error);
      toast.error('Failed to create raw item');
      throw error;
    }
  }

  async updateRawItem(id: string, rawItemData: RawItemUpdateRequest): Promise<RawItem> {
    try {
      const response = await this.makeRequest<{ success: boolean; message: string; data: RawItem }>(
        API_ENDPOINTS.RAW_ITEMS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(rawItemData)
        }
      );
      
      if (response.success) {
        toast.success('Raw item updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update raw item');
      }
    } catch (error) {
      // console.error('Error updating raw item:', error);
      toast.error('Failed to update raw item');
      throw error;
    }
  }

  async deleteRawItem(id: string): Promise<void> {
    try {
      // First, delete all associated raw item variants
      try {
        await rawItemVariantApiService.deleteVariantsByRawItemId(parseInt(id));
      } catch (variantError) {
        // console.warn('Failed to delete raw item variants:', variantError);
        // Continue with raw item deletion even if variant deletion fails
      }

      // Then delete the raw item itself
      const response = await this.makeRequest<{ success: boolean; message: string }>(
        API_ENDPOINTS.RAW_ITEMS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Raw item and associated variants deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete raw item');
      }
    } catch (error) {
      // console.error('Error deleting raw item:', error);
      toast.error('Failed to delete raw item');
      throw error;
    }
  }

  async searchRawItems(searchTerm: string): Promise<RawItem[]> {
    try {
      const response = await this.makeRequest<RawItemResponse>(
        `${API_ENDPOINTS.RAW_ITEMS.SEARCH}?searchTerm=${encodeURIComponent(searchTerm)}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search raw items');
      }
    } catch (error) {
      // console.error('Error searching raw items:', error);
      toast.error('Failed to search raw items');
      throw error;
    }
  }

  async getRateHistory(
    rawItemId: number,
    supplierId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<RawItemRateHistoryDto[]> {
    try {
      const response = await this.makeRequest<ApiResponse<RawItemRateHistoryDto[]>>(
        API_ENDPOINTS.RAW_ITEM_RATE_HISTORY.BY_RAW_ITEM(rawItemId, supplierId, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch rate history');
      }
    } catch (error) {
      // console.error('Error fetching rate history:', error);
      // Don't show toast error as this is optional data
      throw error;
    }
  }

  async getRawItemUsage(rawItemId: number): Promise<any[]> {
    try {
      const response = await this.makeRequest<ApiResponse<any[]>>(
        API_ENDPOINTS.BOM_HEADERS.USAGE(rawItemId.toString()),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch raw item usage');
      }
    } catch (error) {
      // console.error('Error fetching raw item usage:', error);
      // Don't show toast error as this is optional data
      throw error;
    }
  }
}

export const rawItemApiService = new RawItemApiService();
